import React, { useState, useEffect, useContext } from "react";
import "./SummaryModal.css";
import { arrowLeftWhite, NFOLogo } from "../../../../Assets/svgs";
import "../../../../UniversalStyles.css";
import { CircularProgress } from "@mui/material";
import {
  buttonClickTracker,
  getPaymentLinkCheckoutRazorpay,
  getPastOrdersAPI,
  sendOtpAPI,
  verifyOtpAPI,
  CustomerOrder,
  getCouponDiscount,
  CouponCodeResponse,
} from "../../../../Utils/CheckoutAPIs/CheckoutAPIs";
import * as Sentry from "@sentry/react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../../../App";
import Modal from "./Modal";
import SummaryModalBody from "./SummaryModalBody";
import {
  getEventNameFromItemIds,
  trackData,
} from "../../../../Utils/attribution/CheckoutAttribution";

import ReactGA from "react-ga4";
import { experimentTracker } from "../../../../Utils/ExperimentTracker";
import SubtotalHeader from "../Subtotal/SubtotalHeader";
import { configuration } from "../../../../configuration";
import usePricing from "../../../../Utils/hooks/usePricing";
import PricingService from "../../../../Utils/PricingService";

// Coupon State Interface
interface CouponState {
  code: string;
  isApplied: boolean;
  isLoading: boolean;
  discountAmount: number;
  originalAmount: number;
  finalAmount: number;
  error: string;
}

const SummaryModal: React.FC = () => {
  const navigate = useNavigate();
  const [selectedOption, setSelectedOption] = useState<"prepaid" | "cod">(
    "prepaid"
  );
  const { userDetails, checkOutItems, nfoSelected, schoolID, bundleInfo } =
    useContext(CartContext);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(30);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isResendEnabled, setIsResendEnabled] = useState(false);
  const [error, setError] = useState({ otp: "" });
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);

  const [showPastOrdersModal, setShowPastOrdersModal] = useState(false);
  const [pastOrders, setPastOrders] = useState<CustomerOrder[]>([]);

  // Coupon State Management
  const [couponState, setCouponState] = useState<CouponState>({
    code: "",
    isApplied: false,
    isLoading: false,
    discountAmount: 0,
    originalAmount: 0,
    finalAmount: 0,
    error: "",
  });

  const [couponInput, setCouponInput] = useState("");
  const [lastAttemptedCoupon, setLastAttemptedCoupon] = useState("");

  const {
    calculateCurrentAmount,
    getBundleParams,
    getAppliedCouponCode,
    forceUpdate,
  } = usePricing(selectedOption);

  // Check if coupon is enabled for current payment type
  const isCouponEnabledForPaymentType = (): boolean => {
    if (!configuration.coupon.enabled) return false;

    if (selectedOption === "prepaid") {
      return configuration.coupon.enableForPrepaid;
    } else if (selectedOption === "cod") {
      return configuration.coupon.enableForCOD;
    }

    return false;
  };

  // Apply coupon handler
  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      setCouponState((prev) => ({
        ...prev,
        error: "Please enter a coupon code",
      }));
      return;
    }

    setLastAttemptedCoupon(couponInput.trim().toUpperCase());
    setCouponState((prev) => ({ ...prev, isLoading: true, error: "" }));

    try {
      const currentAmount = calculateCurrentAmount();
      const response: CouponCodeResponse = await getCouponDiscount(
        couponInput.trim().toUpperCase(),
        currentAmount
      );

      console.log("Coupon API Response:", response);

      if (response.status === "Success" || response.status === "success") {
        setCouponState({
          code: response.data.coupon_code,
          isApplied: true,
          isLoading: false,
          discountAmount: response.data.discount_amount,
          originalAmount: response.data.priceBeforeDiscount,
          finalAmount: response.data.priceAfterDiscount,
          error: "",
        });

        // Store in session storage
        sessionStorage.setItem("appliedCouponCode", response.data.coupon_code);
        sessionStorage.setItem(
          "couponDiscountAmount",
          response.data.discount_amount.toString()
        );
        sessionStorage.setItem(
          "originalAmount",
          response.data.priceBeforeDiscount.toString()
        );

        // Force update to refresh pricing calculations
        forceUpdate();

        // Dispatch custom event to notify other components
        window.dispatchEvent(new Event("couponUpdated"));
      } else {
        setCouponState((prev) => ({
          ...prev,
          isLoading: false,
          error: response.message || "Invalid coupon code",
        }));
      }
    } catch (error: any) {
      console.error("Coupon API Error:", error);

      let errorMessage = "Invalid coupon code";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.status === 404) {
        errorMessage = "Coupon service is currently unavailable";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      } else if (!error.response) {
        errorMessage = "Network error. Please check your connection.";
      }

      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  };

  // Remove coupon handler
  const handleRemoveCoupon = () => {
    console.log("🗑️ handleRemoveCoupon called");
    console.log("  - Current coupon state before removal:", couponState);

    setCouponState({
      code: "",
      isApplied: false,
      isLoading: false,
      discountAmount: 0,
      originalAmount: 0,
      finalAmount: 0,
      error: "",
    });
    setCouponInput("");
    setLastAttemptedCoupon("");

    // Clear session storage
    console.log("🗑️ Clearing session storage items");
    sessionStorage.removeItem("appliedCouponCode");
    sessionStorage.removeItem("couponDiscountAmount");
    sessionStorage.removeItem("originalAmount");

    // Force update to refresh pricing calculations
    console.log("🔄 Calling forceUpdate");
    forceUpdate();

    // Dispatch custom event to notify other components
    console.log("📡 Dispatching couponUpdated event");
    window.dispatchEvent(new Event("couponUpdated"));

    console.log("✅ handleRemoveCoupon completed");
  };

  // Restore coupon from session storage on component mount
  useEffect(() => {
    const appliedCouponCode = sessionStorage.getItem("appliedCouponCode");
    const couponDiscountAmount = sessionStorage.getItem("couponDiscountAmount");
    const originalAmount = sessionStorage.getItem("originalAmount");

    if (appliedCouponCode && couponDiscountAmount && originalAmount) {
      const discountAmount = parseFloat(couponDiscountAmount);
      const original = parseFloat(originalAmount);

      setCouponState({
        code: appliedCouponCode,
        isApplied: true,
        isLoading: false,
        discountAmount: discountAmount,
        originalAmount: original,
        finalAmount: original - discountAmount,
        error: "",
      });
      setCouponInput(appliedCouponCode);
      setLastAttemptedCoupon(appliedCouponCode);
    }
  }, []);

  // Clear coupon error when user modifies input from last attempted coupon
  useEffect(() => {
    if (
      couponState.error &&
      couponInput.trim().toUpperCase() !== lastAttemptedCoupon
    ) {
      setCouponState((prev) => ({ ...prev, error: "" }));
    }
  }, [couponInput, couponState.error, lastAttemptedCoupon]);

  // Clear coupon when payment type changes and it's not allowed
  useEffect(() => {
    if (couponState.isApplied && !isCouponEnabledForPaymentType()) {
      handleRemoveCoupon();
    }
  }, [selectedOption, couponState.isApplied]);

  // Remove duplicate functions since they're now provided by usePricing hook

  const handleOptionChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPaymentType = event.target.value as "prepaid" | "cod";
    const oldPaymentType = selectedOption;

    console.log("🔍 handleOptionChange triggered:");
    console.log("  - New payment type:", newPaymentType);
    console.log("  - Old payment type:", oldPaymentType);
    console.log("  - Coupon state:", couponState);
    console.log("  - Is coupon applied:", couponState.isApplied);

    // If payment type is changing and coupon is applied, remove the coupon
    if (newPaymentType !== oldPaymentType && couponState.isApplied) {
      console.log(
        "✅ Payment method changed and coupon is applied - removing coupon"
      );
      // Remove existing coupon using the existing function
      handleRemoveCoupon();
    } else if (newPaymentType !== oldPaymentType) {
      console.log("ℹ️ Payment method changed but no coupon applied");
    } else {
      console.log("ℹ️ Same payment method selected");
    }

    // Update payment type
    console.log("🔄 Setting new payment type:", newPaymentType);
    setSelectedOption(newPaymentType);

    if (newPaymentType === "cod") {
      console.log("💳 COD payment method selected");
      // Handle COD specific logic
    } else {
      console.log("💳 Prepaid payment method selected");
      setIsOtpModalOpen(false);
      setIsOtpVerified(true);
    }
  };
  useEffect(() => {
    console.log("checkOutItems", checkOutItems);
    if (checkOutItems.length <= 0) {
      navigate("/Checkout");
    }
  }, [navigate, checkOutItems]);

  const [showAlert, setShowAlert] = useState({
    isAlertVisible: false,
    alert: "",
  });
  const sections = [
    {
      title: "Your Details",
      subTitle: (
        <>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
              {" "}
              First Name:{" "}
            </span>
            {userDetails?.firstName}
          </div>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
              {" "}
              Last Name:{" "}
            </span>
            {userDetails?.lastName}
          </div>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}> Email: </span>
            {userDetails?.email}
          </div>
          <div>
            <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
              {" "}
              Mobile Number:{" "}
            </span>
            {userDetails?.mobileNumber}
          </div>
          <div>
            {nfoSelected && (
              <div>
                <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
                  {" "}
                  Grade:{" "}
                </span>
                {userDetails?.grade}
              </div>
            )}
          </div>
          <div>
            {nfoSelected && (
              <div>
                <span style={{ color: "rgba(44, 106, 177, 0.8)" }}>
                  {" "}
                  School:{" "}
                </span>
                {userDetails?.schoolName}
              </div>
            )}
          </div>
        </>
      ),
      needsVerification: true,
    },
    {
      title: "Shipping Address",
      subTitle: (
        <>
          <div>{userDetails?.flatNo}</div>
          <div>{userDetails?.address}</div>
          <div>{userDetails?.city}</div>
          <div>{userDetails?.state}</div>
          <div>{userDetails?.pincode}</div>
        </>
      ),
      needsVerification: false,
    },

    {
      title: "Billing Address",
      subTitle: (
        <>
          <div>{userDetails?.flatNo}</div>
          <div>{userDetails?.address}</div>
          <div>{userDetails?.city}</div>
          <div>{userDetails?.state}</div>
          <div>{userDetails?.pincode}</div>
        </>
      ),
      needsVerification: false,
    },
  ];

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (otpSent && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpSent, countdown]);

  useEffect(() => {
    if (error.otp) {
      const timer = setTimeout(() => {
        setError((prevError) => ({ ...prevError, otp: "" }));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error.otp]);
  console.log("cod");

  const sendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await sendOtpAPI(userDetails.mobileNumber); // Use the mobile number from userDetails
      if (response.status === "Success") {
        setIsOtpModalOpen(true);
        setIsResendEnabled(false);
        setCountdown(30);
        setOtpSent(true);
        startResendTimer();
      } else {
        setShowAlert({
          isAlertVisible: true,
          alert: "Oops! Something went wrong. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      setShowAlert({
        isAlertVisible: true,
        alert: "Oops! Something went wrong. Please try again later.",
      });
      setError((prevError) => ({
        ...prevError,
        otp: "Failed to send OTP. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const verifyOtp = async () => {
    setIsLoading(true);
    try {
      const response = await verifyOtpAPI(userDetails.mobileNumber, otp);
      const userDetailsAddress =
        userDetails.flatNo +
        " " +
        userDetails.address +
        " " +
        userDetails.city +
        " " +
        userDetails.pincode;
      if (response.status === "Success") {
        setIsVerifying(true);
        setIsOtpModalOpen(false);
        setError((prevError) => ({ ...prevError, otp: "" }));
        setIsOtpVerified(true);
        ReactGA.event(
          experimentTracker.addExperimentParams({
            category: "preorder_initiate_checkout",
            action: "preorder_initiate_checkout",
            label: "preorder_initiate_checkout",
          })
        );
        // Get current item IDs from the actual cart items, not sessionStorage
        const currentItemIds = checkOutItems.map((item) => item.id).join("-");

        trackData("preorder_initiate_checkout", null, null, {
          success: true,
          payment_type: "cod",
          sale: {
            product_type: getEventNameFromItemIds(currentItemIds),
            amount_inr: 0,
            order_id: "",
          },
        });

        getPaymentLinkCheckoutRazorpay({
          firstName: userDetails.firstName,
          lastName: userDetails.lastName,
          phone: userDetails.mobileNumber,
          email: userDetails.email,
          address: userDetailsAddress,
          pincode: userDetails.pincode,
          city: userDetails.city,
          state: userDetails.state,
          items: checkOutItems?.map((item) => item.id).join(",") ?? "",
          paymentType: "cod",
          nfoSelected: nfoSelected,
          ...getBundleParams(),
          couponCode: getAppliedCouponCode(),
          freeShippingExperiment: false,
        })
          .then((response) => {
            if (response.status === "Success") {
              // Get current item IDs from the actual cart items, not sessionStorage
              const currentItemIds = checkOutItems
                .map((item) => item.id)
                .join("-");

              trackData(
                "preorder_user_completed_purchase_successfully",
                null,
                null,
                {
                  success: true,
                  payment_type: "cod",
                  sale: {
                    product_type: getEventNameFromItemIds(currentItemIds),
                    amount_inr: response.data.amount,
                    order_id: response.data.id,
                  },
                }
              );
              ReactGA.event(
                experimentTracker.addExperimentParams({
                  category: "preorder_user_completed_purchase_successfully",
                  action: "preorder_user_completed_purchase_successfully",
                  label: "preorder_user_completed_purchase_successfully",
                  value: response.data.amount,
                })
              );

              buttonClickTracker(
                currentItemIds === "1"
                  ? "personal_finance_handbook_grade_1_and_2_payment_gateway"
                  : "unknown_payment_gateway"
              );

              navigate(
                `/PaymentSuccessful/?order_id=${response.data.id}&${
                  checkOutItems?.map((item) => item.id).join(",") ?? ""
                }`,
                { replace: true }
              );
              setIsLoading(false);
            } else {
              setShowAlert({
                isAlertVisible: true,
                alert: "Oops! Something went wrong. Please try again later.",
              });
              setIsLoading(false);
            }
          })
          .catch((err) => {
            console.log("err", err);
            setShowAlert({
              isAlertVisible: true,
              alert: "Oops! Something went wrong. Please try again later.",
            });
            setIsLoading(false);
          });
      } else {
        setError((prevError) => ({
          ...prevError,
          otp: "Invalid OTP. Please try again.",
        }));
        setIsOtpVerified(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setError((prevError) => ({
        ...prevError,
        otp: "Invalid OTP. Please try again.",
      }));
      setIsOtpVerified(false);
      setIsLoading(false);
    }
  };

  const handleOtp = () => {
    if (!isOtpModalOpen) {
      sendOtp();
    } else if (isResendEnabled) {
      sendOtp();
      setIsVerifying(false);
    }
  };

  const startResendTimer = () => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setIsResendEnabled(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  const [isPurchaseButtonClicked, setIsPurchaseButtonClicked] = useState(false);

  const proceedToBuy = (paymentType: "prepaid" | "cod") => {
    setIsLoading(true);

    console.log("paymentType", paymentType, isOtpVerified);
    sessionStorage.setItem("paymentType", paymentType);
    const pg = sessionStorage.getItem("pg");
    const userDetailsAddress =
      userDetails.flatNo +
      " " +
      userDetails.address +
      " " +
      userDetails.city +
      " " +
      userDetails.pincode;
    if (paymentType === "cod" && !isOtpVerified) {
      getPastOrdersAPI(userDetails.mobileNumber)
        .then((response) => {
          if (response.status === "Success") {
            const customerOrders = response.data.orderedItemIDs;
            const isOrderPresent = checkOutItems.some((item) =>
              customerOrders.includes(item.id.toString())
            );
            if (isOrderPresent) {
              setShowPastOrdersModal(true);
              setPastOrders(response.data.customerOrders);
              setIsLoading(false);
              return;
            } else {
              setIsOtpModalOpen(true);
              // Call sendOtp without affecting loading state since proceedToBuy handles it
              sendOtpAPI(userDetails.mobileNumber)
                .then((otpResponse) => {
                  if (otpResponse.status === "Success") {
                    setIsResendEnabled(false);
                    setCountdown(30);
                    setOtpSent(true);
                    startResendTimer();
                  } else {
                    setShowAlert({
                      isAlertVisible: true,
                      alert: "Failed to send OTP. Please try again later.",
                    });
                  }
                })
                .catch((error) => {
                  console.error("Error sending OTP:", error);
                  setShowAlert({
                    isAlertVisible: true,
                    alert: "Failed to send OTP. Please try again later.",
                  });
                });
              setShowAlert({
                isAlertVisible: true,
                alert: "Please verify OTP to proceed to buy",
              });
              setIsLoading(false);
              return;
            }
          } else {
            setShowAlert({
              isAlertVisible: true,
              alert: "Oops! Something went wrong. Please try again later.",
            });
            setIsLoading(false);
            return;
          }
        })
        .catch((error) => {
          console.error("Error fetching past orders:", error);
          Sentry.captureException(error);
          setShowAlert({
            isAlertVisible: true,
            alert: "Oops! Something went wrong. Please try again later.",
          });
          setIsLoading(false);
          return;
        });
      return;
    }
    if (pg === "razorpay") {
      getPaymentLinkCheckoutRazorpay({
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        email: userDetails.email,
        phone: userDetails.mobileNumber,
        address: userDetailsAddress,
        state: userDetails.state,
        city: userDetails.city,
        pincode: userDetails.pincode,
        items: checkOutItems?.map((item) => item.id).join(",") ?? "",
        paymentType: paymentType,
        nfoSelected: nfoSelected,
        schoolName: userDetails.schoolName,
        grade: userDetails.grade,
        schoolID: null,
        ...getBundleParams(),
        couponCode: getAppliedCouponCode(),
      })
        .then((response) => {
          if (paymentType === "prepaid") {
            if (response.status === "Success") {
              const { amount, currency, id, notes } = response.data;
              sessionStorage.setItem(
                "transactionAmount",
                (amount / 100).toString()
              );
              // Get current item IDs from the actual cart items, not sessionStorage
              const currentItemIds = checkOutItems
                .map((item) => item.id)
                .join("-");

              ReactGA.event(
                experimentTracker.addExperimentParams({
                  category: "preorder_initiate_checkout",
                  action: "preorder_initiate_checkout",
                  label: "preorder_initiate_checkout",
                  value: amount,
                })
              );

              trackData("preorder_initiate_checkout", null, null, {
                success: true,
                payment_type: "prepaid",
                sale: {
                  product_type: getEventNameFromItemIds(currentItemIds),
                  amount_inr: amount,
                  order_id: id,
                },
              });

              buttonClickTracker(
                currentItemIds === "1"
                  ? "personal_finance_handbook_grade_1_and_2_payment_gateway"
                  : "unknown_payment_gateway"
              );

              const options = {
                key: "rzp_live_B6xhot5HoXKLQE",
                amount: amount,
                currency: currency,
                name: "National Finance Olympiad",
                description: "National Finance Olympiad Books",
                image:
                  "https://cdn.razorpay.com/logos/BUVwvgaqVByGp2_large.jpg",
                order_id: id,
                prefill: {
                  name: userDetails.firstName + " " + userDetails.lastName,
                  email: userDetails.email,
                  contact: userDetails.mobileNumber,
                },
                theme: {
                  color: "rgba(55, 108, 170, 1)",
                },
                method: {
                  netbanking: true,
                  card: true,
                  wallet: false,
                  upi: true,
                  paylater: false,
                },
                handler: async (response: any) => {
                  navigate(
                    `/PaymentSuccessful/?order_id=${id}&${
                      checkOutItems?.map((item) => item.id).join(",") ?? ""
                    }`
                  );
                },
                modal: {
                  ondismiss: function () {
                    console.log("this is ondismiss");
                  },
                },
              };
              const razorpay = new (window as any).Razorpay(options);
              razorpay.open();
              setIsPurchaseButtonClicked(false);
            } else {
              setShowAlert({
                isAlertVisible: true,
                alert: "Oops! Something went wrong. Please try again later.",
              });
            }
          } else {
            // COD payment handling
            if (response.status === "Success") {
              // Get current item IDs from the actual cart items, not sessionStorage
              const currentItemIds = checkOutItems
                .map((item) => item.id)
                .join("-");
              sessionStorage.setItem(
                "transactionAmount",
                response.data.amount.toString()
              );
              sessionStorage.setItem("transactionId", response.data.id);

              // Track COD purchase success
              trackData(
                "preorder_user_completed_purchase_successfully",
                null,
                null,
                {
                  success: true,
                  payment_type: "cod",
                  sale: {
                    product_type: getEventNameFromItemIds(currentItemIds),
                    amount_inr: response.data.amount,
                    order_id: response.data.id,
                  },
                }
              );

              ReactGA.event(
                experimentTracker.addExperimentParams({
                  category: "preorder_user_completed_purchase_successfully",
                  action: "preorder_user_completed_purchase_successfully",
                  label: "preorder_user_completed_purchase_successfully",
                  value: response.data.amount,
                })
              );

              buttonClickTracker(
                currentItemIds === "1"
                  ? "personal_finance_handbook_grade_1_and_2_payment_gateway"
                  : "unknown_payment_gateway"
              );

              // Navigate to success page for COD
              navigate(
                `/PaymentSuccessful/?order_id=${response.data.id}&${
                  checkOutItems?.map((item) => item.id).join(",") ?? ""
                }`,
                { replace: true }
              );
            } else {
              setShowAlert({
                isAlertVisible: true,
                alert: "Oops! Something went wrong. Please try again later.",
              });
              // Reset OTP verification state on failure
              setIsOtpVerified(false);
              setIsOtpModalOpen(false);
            }
          }
        })
        .catch((err) => {
          setIsPurchaseButtonClicked(false);
          console.log(err);
          Sentry.captureException(err);
          setShowAlert({
            isAlertVisible: true,
            alert: "Oops! Something went wrong. Please try again later.",
          });
          // Reset OTP verification state on error
          if (paymentType === "cod") {
            setIsOtpVerified(false);
            setIsOtpModalOpen(false);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setShowAlert({
        isAlertVisible: false,
        alert: "",
      });
    }, 5000);
  }, [showAlert.isAlertVisible]);

  const getOrderDescription = (): string => {
    // Use actual cart items instead of sessionStorage
    const itemIdsArray = checkOutItems.map((item) => item.id);
    const hasItem1 = itemIdsArray.includes(1);
    const hasItem2 = itemIdsArray.includes(2);
    const hasItem3 = itemIdsArray.includes(3);

    if (hasItem3) {
      if (hasItem1 && !hasItem2) {
        return "NFO Registration with Personal Finance Handbook for Grade 6-8";
      } else if (!hasItem1 && hasItem2) {
        return "NFO Registration with Personal Finance Handbook for Grade 10-12";
      } else if (hasItem1 && hasItem2) {
        return "NFO Registration with Personal Finance Handbook for Grade 6-8 and 10-12";
      } else {
        return "NFO Registration";
      }
    } else if (hasItem1 && !hasItem2) {
      return "Personal Finance Handbook for Grade 6-8";
    } else if (!hasItem1 && hasItem2) {
      return "Personal Finance Handbook for Grade 10-12";
    } else if (hasItem1 && hasItem2) {
      return "Personal Finance Handbook for Grade 6-8 and 10-12";
    } else {
      return "Personal Finance Handbook";
    }
  };
  console.log("getOrderDescription", getOrderDescription());

  return (
    <div className={`summary-modal-backdrop`}>
      <SubtotalHeader />
      <div className="summary-modal-content">
        <div className="summary-modal-header">
          <div className="summary-modal-header-left">
            <img
              onClick={() => {
                navigate(-1);
              }}
              className="summary-modal-header-left-icon"
              alt="arrowLeftWhite"
              src={arrowLeftWhite}
            />
            <div className="summary-modal-header-left-text">Order Summary</div>
          </div>
          <div className="summary-modal-header-right">
            <img className="universal-NFO-logo" alt="NFOLogo" src={NFOLogo} />
          </div>
        </div>
        <SummaryModalBody
          sections={sections}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          handleOptionChange={handleOptionChange}
          checkOutItems={checkOutItems}
          proceedToBuy={proceedToBuy}
          isPurchaseButtonClicked={isPurchaseButtonClicked}
          showAlert={showAlert}
          couponState={couponState}
          couponInput={couponInput}
          setCouponInput={setCouponInput}
          handleApplyCoupon={handleApplyCoupon}
          handleRemoveCoupon={handleRemoveCoupon}
          isCouponEnabledForPaymentType={isCouponEnabledForPaymentType}
        />

        {isOtpModalOpen && (
          <Modal
            isOpen={isOtpModalOpen}
            onClose={() => setIsOtpModalOpen(false)}
            aria-labelledby="otp-verification-title"
            aria-describedby="otp-verification-description"
          >
            <div className="otp-modal-content">
              <h3 id="otp-verification-title">OTP Verification</h3>
              <input
                className={`email-input`}
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="Enter OTP"
                aria-label="Enter OTP"
              />
              <button
                className="verify-button"
                onClick={verifyOtp}
                disabled={isVerifying}
                aria-busy={isVerifying}
              >
                {isVerifying ? "Verifying..." : "Verify OTP"}
              </button>
              <button
                className="verify-button"
                onClick={handleOtp}
                disabled={!isResendEnabled}
              >
                Resend OTP {countdown > 0 && `(${countdown}s)`}
              </button>
              {error.otp && (
                <div className="error-message" role="alert">
                  {error.otp}
                </div>
              )}
              <button
                className="verify-button close-button"
                onClick={() => setIsOtpModalOpen(false)}
              >
                &times;
              </button>
            </div>
          </Modal>
        )}
        {showPastOrdersModal && (
          <Modal
            isOpen={showPastOrdersModal}
            onClose={() => setShowPastOrdersModal(false)}
            aria-labelledby="past-orders-title"
            aria-describedby="past-orders-description"
          >
            <div className="otp-modal-content">
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "600",
                    maxWidth: "80%",
                    textAlign: "left",
                    marginTop: "5px",
                    marginBottom: "5px",
                  }}
                >
                  You have previously ordered these books.
                </h3>
                <button
                  className="verify-button close-button"
                  onClick={() => setShowPastOrdersModal(false)}
                >
                  &times;
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    maxWidth: "80%",
                    textAlign: "left",
                    marginTop: "10px",
                  }}
                >
                  Name
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    maxWidth: "80%",
                    textAlign: "left",
                    marginTop: "10px",
                  }}
                >
                  Last purchased on
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "15px",
                  fontSize: "14px",
                  fontWeight: "500",
                  justifyContent: "space-between",
                  marginTop: "15px",
                }}
              >
                {pastOrders
                  .slice(0, 6)
                  .reverse()
                  .map((order) => (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "10px",
                      }}
                      key={Math.random()}
                    >
                      <div
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "14px",
                          maxWidth: "50%",
                          width: "50%",
                          textAlign: "left",
                        }}
                      >
                        {order.products}
                      </div>
                      <div
                        style={{
                          width: "50%",
                          height: 2,
                          backgroundImage:
                            "radial-gradient(circle, rgba(44, 106, 177, 0.5) 1px, transparent 1px)",
                          backgroundSize: "4px 4px",
                        }}
                      />
                      <div>
                        {new Date(order.orderDate).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                  ))}
              </div>
              <button
                style={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: "pointer",
                  backgroundColor: "rgba(44, 106, 177, 1)",
                  alignItems: "center",
                  width: "100%",
                  borderWidth: "0px",
                  borderRadius: "8px",
                  marginBottom: "0px",
                  padding: "10px",
                  color: "#FFF",
                  fontSize: "14px",
                  fontFamily: "OggTextMedium",
                  fontWeight: "600",
                  marginTop: "20px",
                }}
                onClick={() => {
                  setShowPastOrdersModal(false);
                  setIsOtpModalOpen(true);
                  sendOtp();
                  setShowAlert({
                    isAlertVisible: true,
                    alert: "Please verify OTP to proceed to buy",
                  });
                }}
                disabled={isVerifying}
                aria-busy={isVerifying}
              >
                {"Proceed to Buy Again"}
              </button>
            </div>
          </Modal>
        )}
      </div>
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(251,251,251,0.5)",
            zIndex: 1000,
          }}
        >
          <CircularProgress />
        </div>
      )}
    </div>
  );
};

export default SummaryModal;
